import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import authService from "../services/authService";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";  // Import Swal

const ViewScoreboard = () => {
  const { studentId } = useParams();
  const [user, setUser] = useState(null);
  const [scores, setScores] = useState([]);
  const [studentProfile, setStudentProfile] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
    } else {
      setError("User not authenticated. Please log in.");
      Swal.fire({
        icon: "error",
        title: "Authentication Error",
        text: "Please log in to view this page.",
      });
    }
  }, []);

  useEffect(() => {
    if (user && studentId) {
      // Show loading Swal
      Swal.fire({
        title: "Loading...",
        text: "Fetching data, please wait",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const fetchProfile = async () => {
        try {
          const response = await axios.get(
            `https://doshfebackend.teemah.com.ng/api/auth/profile/`,
            {
              headers: { Authorization: `Bearer ${user.access}` },
            }
          );
          setStudentProfile(response.data);
        } catch (err) {
          setError("Failed to fetch student profile.");
        }
      };

      const fetchScores = async () => {
        try {
          const response = await axios.get(
            `https://doshfebackend.teemah.com.ng/api/auth/scoreboard/?student_id=${studentId}`,
            {
              headers: { Authorization: `Bearer ${user.access}` },
            }
          );
          setScores(response.data);
        } catch (err) {
          setError("Failed to fetch scores. Please try again.");
        } finally {
          Swal.close(); // Close the loading modal when done
        }
      };

      fetchProfile();
      fetchScores();
    }
  }, [user, studentId]);

  const calculateAverageScore = () => {
    if (scores.length === 0) return 0;
    const totalScore = scores.reduce((sum, score) => sum + score.score, 0);
    return (totalScore / scores.length).toFixed(2);
  };

  // Group scores by Course
  const groupedScores = scores.reduce((acc, score) => {
    if (!acc[score.presentation_title]) {
      acc[score.presentation_title] = [];
    }
    acc[score.presentation_title].push(score);
    return acc;
  }, {});

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-100">
      <Link
        className="bg-red-600 text-white px-4 py-2 rounded-lg mb-4 hover:bg-red-700 transition p-4 mt-20"
        to="/dashboard"
      >
        Return To Dashboard
      </Link>

      <div className="mb-3 text-3xl text-white bg-blue-600 p-4 rounded-lg font-extrabold">
        <h2>Score Board</h2>
      </div>

      <div className="bg-white shadow-lg rounded-lg p-6 w-full max-w-4xl">
        {/* Header Section */}
        <div className="flex justify-between items-center mb-2 p-6">
          <img src="/dca.png" alt="Logo" className="h-20" />
          <h2 className="text-xl font-bold text-blue-600">Dosh Coding Academy</h2>
        </div>

        <hr />

        {/* Student Profile Section */}
        {studentProfile && (
          <div className="flex flex-col md:flex-row md:justify-between items-center border-b pb-4 my-4 space-y-4 md:space-y-0">
            {/* Student Profile Section */}
            <div className="flex flex-col md:flex-row items-center space-x-0 md:space-x-4 text-center md:text-left">
              <img
                src={studentProfile.profile_picture || "/default-profile.png"}
                alt="Profile"
                className="h-20 w-20 md:h-24 md:w-24 border-4 border-blue-500 shadow-md mx-auto md:mx-0"
              />
              <div className="mt-2 md:mt-0">
                <h3 className="text-lg md:text-xl font-semibold text-blue-800">
                  {studentProfile.first_name} {studentProfile.last_name}
                </h3>
                <p className="text-sm md:text-base text-gray-600">
                  <span className="font-semibold">Grade:</span> {studentProfile.grade}
                </p>
                <p className="text-sm md:text-base text-gray-600">
                  <span className="font-semibold">Email:</span> {studentProfile.email}
                </p>
                <p className="text-sm md:text-base text-gray-600">
                  <span className="font-semibold">Location:</span> {studentProfile.location}
                </p>
              </div>
            </div>

            {/* Average Score Card */}
            <div className="bg-gradient-to-br from-blue-400 to-blue-600 text-white p-4 md:p-6 rounded-lg shadow-lg text-center w-full md:w-auto">
              <h4 className="text-base md:text-lg font-bold">Average Score</h4>
              <p className="text-2xl md:text-3xl font-extrabold">{calculateAverageScore()}</p>
            </div>
          </div>
        )}

        <hr />

        {/* Scoreboard Table */}
        {scores.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300 shadow-md mt-4">
              <thead>
                <tr className="bg-blue-600 text-white">
                  <th className="border p-3">S/N</th>
                  <th className="border p-3">Course</th>
                  <th className="border p-3">S/N</th>
                  <th className="border p-3">Type</th>
                  <th className="border p-3">Score</th>
                  <th className="border p-3">Total Marks</th>
                  <th className="border p-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(groupedScores)
                  .reverse()
                  .map((course, courseIndex) => (
                    <React.Fragment key={course}>
                      <tr className="bg-gray-200 font-bold">
                        <td className="border p-3">{courseIndex + 1}</td>
                        <td className="border p-3" colSpan="6">
                          {course}
                        </td>
                      </tr>
                      {groupedScores[course].map((score, typeIndex) => (
                        <tr key={score.id} className="border hover:bg-blue-100 transition">
                          <td className="border p-3"></td>
                          <td className="border p-3"></td>
                          <td className="border p-3">{typeIndex + 1}</td>
                          <td className="border p-3">{score.presentation_type}</td>
                          <td className="border p-3 font-semibold bg-yellow-100 hover:bg-blue-100">
                            {score.score}
                          </td>
                          <td className="border p-3">{score.total_marks}</td>
                          <td className="border p-3">{new Date(score.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-700">No scores found for this student.</p>
        )}
      </div>
    </div>
  );
};

export default ViewScoreboard;
